/**
 * Gate 2 — Execution REST client.
 */
import axios from "axios";
import { API_BASE_URL } from "../config";

const base = (projectId) => `${API_BASE_URL}/projects/${projectId}/gate2`;

/** Load project row (includes gate2_data when migration applied). */
export async function fetchProjectGate2Data(projectId) {
  const res = await axios.get(`${API_BASE_URL}/projects/${projectId}`);
  return res.data?.data?.gate2_data || null;
}

/** Persist Gate 2 wizard + scorecard snapshot (same shape as session snapshot). */
export async function saveProjectGate2Data(projectId, snapshot) {
  const res = await axios.put(`${API_BASE_URL}/projects/${projectId}/gate2`, snapshot);
  return res.data;
}

/** The vector store connection verified in Gate 1 readiness (no credentials). */
export async function fetchGate2VectorConfig(projectId) {
  const res = await axios.get(`${base(projectId)}/vector-config`);
  return res.data;
}

/** Non-secret vector / API / traces connections saved in Gate 1. */
export async function fetchGate2ConnectionConfig(projectId) {
  const res = await axios.get(`${base(projectId)}/connection-config`);
  return res.data;
}

export async function fetchRagMetrics(projectId) {
  const res = await axios.get(`${base(projectId)}/rag-metrics`);
  return res.data;
}

/**
 * Query the declared vector database once per in-scope test case.
 * @param {object} payload { suite_ids | test_case_ids, vector_store_config, credentials }
 */
export async function retrieveExecutionContext(projectId, payload) {
  const res = await axios.post(`${base(projectId)}/execution/retrieve-context`, payload);
  return res.data;
}

/**
 * Collect evidence from the connectors the selected metrics need
 * (vector store, application API, and/or traces).
 */
export async function collectExecutionEvidence(projectId, payload) {
  const res = await axios.post(`${base(projectId)}/execution/collect-evidence`, payload);
  return res.data;
}

/**
 * Score one suite's retrieved cases against the metric the suite measures.
 * @param {object} payload { suite_id, suite_name, metric_id?, cases[] }
 */
export async function scoreExecutionMetric(projectId, payload) {
  const res = await axios.post(`${base(projectId)}/execution/score`, payload);
  return res.data;
}

export async function listExecutionRuns(projectId, limit = 50) {
  const res = await axios.get(`${base(projectId)}/execution/runs`, { params: { limit } });
  return res.data;
}

export async function getExecutionRunResults(projectId, runId) {
  const res = await axios.get(`${base(projectId)}/execution/runs/${runId}`);
  return res.data;
}

/**
 * Ask the backend to print the Decision trust-score breakdown.
 * The score is still computed in the browser; this only writes the log lines.
 */
export async function logTrustScoreBreakdown(projectId, breakdown) {
  const res = await axios.post(`${base(projectId)}/execution/trust-score-log`, breakdown);
  return res.data;
}
